import axios from "axios";
import { API_CONFIG } from "../config";

interface User {
  id: string;
  userName: string;
  password: string;
  firstName: string;
  lastName: string;
  role: string;
  businessId: string;
  email: string;
  phone: string;
  isActive: boolean;
  createdDate: Date;
  lastUpdatedDate: Date;
  lastUpdatedBy: string;
}

class UserApiClient {
  /**
   * Fetch users for a specific business
   */
  static async fetchUsers(businessId: string): Promise<User[]> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return [];
      }

      if (!businessId) {
        console.log("No business ID provided");
        return [];
      }

      const response = await axios.get(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS(businessId)}`,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Users response:", response.data);

      // More robust data handling
      const userData = Array.isArray(response.data)
        ? response.data
        : response.data?.data
        ? response.data.data
        : [];

      return userData.map((user: any) => ({
        id: user?.id,
        userName: user?.userName,
        email: user?.email,
        phone: user?.phone,
        firstName: user?.firstName,
        lastName: user?.lastName,
        role: user?.role,
        businessId: user?.businessId,
        isActive: user?.isActive,
        createdDate: user?.createdDate
          ? new Date(user.createdDate)
          : new Date(),
        lastUpdatedDate: user?.lastUpdatedDate
          ? new Date(user.lastUpdatedDate)
          : new Date(),
        lastUpdatedBy: user?.lastUpdatedBy,
      }));
    } catch (error: any) {
      console.error("Error fetching users:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      return [];
    }
  }

  /**
   * Create a new user
   */
  static async createUser(userData: any): Promise<User> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }

      const response = await axios.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ADD_USER(
          userData.businessId
        )}`,
        userData,
        {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json-patch+json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Create user response:", response.data);

      // Check if response is successful (200-299)
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        // Handle non-success status codes
        const errorMessage =
          response.data?.Message ||
          response.data?.message ||
          "Failed to create user";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error creating user:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      // If it's an axios error, throw with the API message
      if (error.response) {
        const errorMessage =
          error.response.data?.Message ||
          error.response.data?.message ||
          `HTTP ${error.response.status}: Failed to create user`;
        throw new Error(errorMessage);
      }

      // Re-throw the original error
      throw error;
    }
  }

  /**
   * Update an existing user
   */
  static async updateUser(
    isFromUserSetting: boolean,
    userId: string,
    userData: any
  ): Promise<User> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }
      let updateUserUrl = `${
        API_CONFIG.BASE_URL
      }${API_CONFIG.ENDPOINTS.UPDATE_USER_ADMIN(userData.businessId, userId)}`;
      if (isFromUserSetting) {
        //user setting doesn't need business id as user himself updating his profile
        updateUserUrl = `${
          API_CONFIG.BASE_URL
        }${API_CONFIG.ENDPOINTS.UPDATE_USER(userId)}`;
      }
      const response = await axios.put(updateUserUrl, userData, {
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Update user response:", response.data);

      // Check if response is successful (200-299)
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        // Handle non-success status codes
        const errorMessage =
          response.data?.Message ||
          response.data?.message ||
          "Failed to update user";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error updating user:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      // If it's an axios error, throw with the API message
      if (error.response) {
        const errorMessage =
          error.response.data?.Message ||
          error.response.data?.message ||
          `HTTP ${error.response.status}: Failed to update user`;
        throw new Error(errorMessage);
      }

      // Re-throw the original error
      throw error;
    }
  }

  /**
   * Reset user password
   */
  static async forceResetPassword(
    businessId: string,
    userId: string,
    password: string
  ): Promise<any> {
    const resetPasswordUrl = `${
      API_CONFIG.BASE_URL
    }${API_CONFIG.ENDPOINTS.RESET_PASSWORD_ADMIN(businessId, userId)}`;
    return UserApiClient.resetPassword(resetPasswordUrl, password);
  }

  static async selfResetPassword(
    userId: string,
    password: string
  ): Promise<any> {
    const resetPasswordUrl = `${
      API_CONFIG.BASE_URL
    }${API_CONFIG.ENDPOINTS.RESET_PASSWORD(userId)}`;
    return UserApiClient.resetPassword(resetPasswordUrl, password);
  }

  static async resetPassword(
    resetPasswordUrl: string,
    password: string
  ): Promise<any> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }

      const response = await axios.put(
        resetPasswordUrl,
        { password },
        {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json-patch+json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Reset password response:", response.data);

      // Check if response is successful (200-299)
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        // Handle non-success status codes
        const errorMessage =
          response.data?.Message ||
          response.data?.message ||
          "Failed to reset password";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error resetting password:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      // If it's an axios error, throw with the API message
      if (error.response) {
        const errorMessage =
          error.response.data?.Message ||
          error.response.data?.message ||
          `HTTP ${error.response.status}: Failed to reset password`;
        throw new Error(errorMessage);
      }

      // Re-throw the original error
      throw error;
    }
  }
}

export default UserApiClient;
